import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    gamertag: '',
    platform: '',
    inviteCode: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [codeValidation, setCodeValidation] = useState({ valid: null, message: '', teamName: null });
  const [validatingCode, setValidatingCode] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const validateInviteCode = async () => {
    if (!formData.inviteCode) {
      setCodeValidation({ valid: null, message: '', teamName: null });
      return;
    }

    setValidatingCode(true);
    try {
      const response = await axios.post(`${API_URL}/invite-codes/validate`, {
        code: formData.inviteCode
      });
      
      setCodeValidation({
        valid: true,
        message: response.data.teamName 
          ? `Valid code! You'll be assigned to ${response.data.teamName}`
          : 'Valid code! An admin will assign you to a team.',
        teamName: response.data.teamName
      });
    } catch (err) {
      setCodeValidation({
        valid: false,
        message: err.response?.data?.error || 'Invalid invite code',
        teamName: null
      });
    } finally {
      setValidatingCode(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (!formData.inviteCode) {
      setError('Invite code is required');
      return;
    }

    setLoading(true);

    try {
      const userData = {
        username: formData.username,
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        gamertag: formData.gamertag,
        platform: formData.platform || null,
        inviteCode: formData.inviteCode
      };

      const user = await register(userData);
      
      // Show success message and redirect
      if (user.role === 'team_owner') {
        navigate('/admin', { 
          state: { message: `Welcome! You've been assigned to ${user.team?.name}` } 
        });
      } else {
        navigate('/admin', { 
          state: { message: 'Registration successful! An admin will assign you to a team soon.' } 
        });
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-700 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="flex justify-center mb-6">
            <img 
              src="https://www.2kbundesliga.de/wp-content/uploads/2022/01/cropped-cropped-2kbuli-header_06.png" 
              alt="2K Bundesliga Logo"
              className="h-20 w-auto object-contain"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-gray-100">
            Team Owner Registration
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
            You need a valid invite code to register
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 dark:text-red-400 px-4 py-3 rounded relative">
              {error}
            </div>
          )}

          <div className="rounded-md shadow-sm space-y-4">
            {/* Invite Code */}
            <div>
              <label htmlFor="inviteCode" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Invite Code *
              </label>
              <div className="flex gap-2">
                <input
                  id="inviteCode"
                  name="inviteCode"
                  type="text"
                  required
                  value={formData.inviteCode}
                  onChange={handleChange}
                  onBlur={validateInviteCode}
                  className="appearance-none rounded-md relative block flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-nba-blue focus:border-nba-blue sm:text-sm dark:bg-gray-700 dark:text-white dark:border-gray-600"
                  placeholder="Enter your invite code"
                />
                <button
                  type="button"
                  onClick={validateInviteCode}
                  disabled={validatingCode || !formData.inviteCode}
                  className="px-4 py-2 bg-nba-blue text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {validatingCode ? 'Checking...' : 'Validate'}
                </button>
              </div>
              {codeValidation.valid === true && (
                <p className="mt-1 text-sm text-green-600 dark:text-green-400">{codeValidation.message}</p>
              )}
              {codeValidation.valid === false && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{codeValidation.message}</p>
              )}
            </div>

            {/* First Name */}
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                First Name *
              </label>
              <input
                id="firstName"
                name="firstName"
                type="text"
                required
                value={formData.firstName}
                onChange={handleChange}
                className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-nba-blue focus:border-nba-blue sm:text-sm dark:bg-gray-700 dark:text-white dark:border-gray-600"
                placeholder="John"
              />
            </div>

            {/* Username */}
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Username *
              </label>
              <input
                id="username"
                name="username"
                type="text"
                required
                value={formData.username}
                onChange={handleChange}
                className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-nba-blue focus:border-nba-blue sm:text-sm dark:bg-gray-700 dark:text-white dark:border-gray-600"
                placeholder="johndoe"
              />
            </div>

            {/* Gamertag */}
            <div>
              <label htmlFor="gamertag" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                NBA 2K Gamertag *
              </label>
              <input
                id="gamertag"
                name="gamertag"
                type="text"
                required
                value={formData.gamertag}
                onChange={handleChange}
                className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-nba-blue focus:border-nba-blue sm:text-sm dark:bg-gray-700 dark:text-white dark:border-gray-600"
                placeholder="Your NBA 2K username"
              />
            </div>

            {/* Platform */}
            <div>
              <label htmlFor="platform" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Gaming Platform *
              </label>
              <select
                id="platform"
                name="platform"
                required
                value={formData.platform}
                onChange={handleChange}
                className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-nba-blue focus:border-nba-blue sm:text-sm bg-white dark:bg-gray-800 dark:bg-gray-700 dark:text-white dark:border-gray-600"
              >
                <option value="">Select your platform</option>
                <option value="Xbox">🎮 Xbox Series S/X</option>
                <option value="PlayStation">🎯 PlayStation 5</option>
              </select>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Select the console you'll be playing NBA 2K on
              </p>
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email Address *
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-nba-blue focus:border-nba-blue sm:text-sm dark:bg-gray-700 dark:text-white dark:border-gray-600"
                placeholder="john@example.com"
              />
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Password *
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                value={formData.password}
                onChange={handleChange}
                className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-nba-blue focus:border-nba-blue sm:text-sm dark:bg-gray-700 dark:text-white dark:border-gray-600"
                placeholder="At least 6 characters"
              />
            </div>

            {/* Confirm Password */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Confirm Password *
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                required
                value={formData.confirmPassword}
                onChange={handleChange}
                className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-nba-blue focus:border-nba-blue sm:text-sm dark:bg-gray-700 dark:text-white dark:border-gray-600"
                placeholder="Confirm your password"
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading || codeValidation.valid === false}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-nba-blue hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-nba-blue disabled:bg-gray-400 disabled:cursor-not-allowed dark:bg-gray-700 dark:text-white dark:border-gray-600"
            >
              {loading ? 'Registering...' : 'Register'}
            </button>
          </div>

          <div className="text-center">
            <Link to="/login" className="text-nba-blue hover:underline font-semibold">
              Already have an account? Login
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Register;
